import { token } from '../../theme';
import { onThemeChange } from '../../theme';

/**
 * Segmented LED level meter (dBFS-style).
 *
 * Reads instantaneous + peak-hold from an AnalyserNode's time-domain RMS.
 * Segments light up green → amber → red as level rises.
 */
export class LedMeter {
  readonly el: HTMLDivElement;
  private analyser: AnalyserNode | null = null;
  private buf: Uint8Array<ArrayBuffer> | null = null;
  private animId: number | null = null;
  private offThemeChange: (() => void) | null = null;

  private readonly segments = 24;
  private readonly thresholdMid = 0.6; // start amber
  private readonly thresholdHigh = 0.85; // start red
  private peak = 0;
  private peakDecay = 0.005;
  private current = 0;
  private smoothing = 0.6;

  private segmentEls: HTMLDivElement[] = [];
  private peakEl: HTMLDivElement;
  private valueEl: HTMLSpanElement;

  constructor() {
    this.el = document.createElement('div');
    this.el.className = 'led-meter';

    const scale = document.createElement('div');
    scale.className = 'led-meter__scale';
    for (let i = 0; i < this.segments; i++) {
      const seg = document.createElement('div');
      seg.className = 'led-meter__seg';
      seg.dataset.zone =
        i / (this.segments - 1) >= this.thresholdHigh
          ? 'high'
          : i / (this.segments - 1) >= this.thresholdMid
          ? 'mid'
          : 'low';
      scale.appendChild(seg);
      this.segmentEls.push(seg);
    }

    this.peakEl = document.createElement('div');
    this.peakEl.className = 'led-meter__peak';
    scale.appendChild(this.peakEl);

    const labels = document.createElement('div');
    labels.className = 'led-meter__labels';
    labels.innerHTML = `
      <span>-60</span><span>-40</span><span>-20</span><span>-12</span><span>-6</span><span>0</span>
    `;

    const head = document.createElement('div');
    head.className = 'led-meter__head';
    const title = document.createElement('span');
    title.className = 'led-meter__title';
    title.textContent = 'LEVEL';
    this.valueEl = document.createElement('span');
    this.valueEl.className = 'led-meter__value';
    this.valueEl.textContent = '-∞ dB';
    head.appendChild(title);
    head.appendChild(this.valueEl);

    this.el.appendChild(head);
    this.el.appendChild(scale);
    this.el.appendChild(labels);

    this.offThemeChange = onThemeChange(() => {
      /* CSS-driven; nothing to recache */
    });
    void token; // keep import paths consistent if we need explicit JS-side colors later
  }

  bind(analyser: AnalyserNode | null): void {
    this.analyser = analyser;
    if (analyser) this.buf = new Uint8Array(analyser.fftSize);
  }

  start(): void {
    if (this.animId !== null) return;
    const tick = () => {
      this.update();
      this.animId = requestAnimationFrame(tick);
    };
    this.animId = requestAnimationFrame(tick);
  }

  stop(): void {
    if (this.animId !== null) cancelAnimationFrame(this.animId);
    this.animId = null;
    this.current = 0;
    this.peak = 0;
    this.render();
  }

  destroy(): void {
    this.stop();
    this.offThemeChange?.();
  }

  private update(): void {
    if (!this.analyser || !this.buf) {
      this.current *= 1 - this.smoothing;
      this.peak = Math.max(0, this.peak - this.peakDecay);
      this.render();
      return;
    }
    this.analyser.getByteTimeDomainData(this.buf);
    let sumSq = 0;
    for (let i = 0; i < this.buf.length; i++) {
      const v = this.buf[i] / 128.0 - 1.0;
      sumSq += v * v;
    }
    const rms = Math.sqrt(sumSq / this.buf.length);
    // Map RMS [0..0.5] → [0..1] visual level (0.5 RMS ≈ very loud)
    const lvl = Math.min(1, rms * 2.4);
    this.current = this.current * (1 - this.smoothing) + lvl * this.smoothing;
    if (this.current > this.peak) this.peak = this.current;
    else this.peak = Math.max(0, this.peak - this.peakDecay);
    this.render();
  }

  private render(): void {
    const lit = Math.round(this.current * this.segments);
    const peakSeg = Math.max(0, Math.round(this.peak * this.segments) - 1);
    for (let i = 0; i < this.segments; i++) {
      const on = i < lit;
      const seg = this.segmentEls[i];
      seg.classList.toggle('is-on', on);
    }
    // Peak indicator position
    if (this.peak <= 0) {
      this.peakEl.style.opacity = '0';
    } else {
      const pct = (peakSeg / this.segments) * 100;
      this.peakEl.style.opacity = '1';
      this.peakEl.style.left = `calc(${pct}% + 1px)`;
      // Peak color reflects zone
      const ratio = peakSeg / (this.segments - 1);
      this.peakEl.dataset.zone =
        ratio >= this.thresholdHigh
          ? 'high'
          : ratio >= this.thresholdMid
          ? 'mid'
          : 'low';
    }

    if (this.current <= 0.0005) {
      this.valueEl.textContent = '-∞ dB';
    } else {
      const db = 20 * Math.log10(this.current);
      this.valueEl.textContent = `${db.toFixed(1)} dB`;
    }
  }
}
